'use client';

import { useCallback, useMemo, useState } from 'react';
import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useApiClient } from '../contexts/ApiContext';
import { queryKeys } from '../lib/queryClient';
import { getApiBaseUrl } from '../utils/apiBaseUrl';
import type {
  SupportUserMessage,
  SupportUserMessageList,
} from '../services/generated/Api';
import { useAuthedQuery } from './useAuthedQuery';

const PAGE_SIZE = 30;
/** While the panel is open the conversation is the screen — poll it. */
const OPEN_POLL_MS = 7_000;
/** With the panel closed only the badge matters, so ask far less often. */
const CLOSED_POLL_MS = 45_000;

export const SUPPORT_UPLOAD_LIMIT_BYTES = 5 * 1024 * 1024;

export function supportAttachmentUrl(attachmentId: string): string {
  return `${getApiBaseUrl()}/api/users/support/attachments/${attachmentId}/content`;
}

/**
 * Unread badge and write permission. Cheap enough to keep polling in the
 * background so the launcher can show that support answered while the
 * person was doing something else.
 */
export function useSupportSummary(isPanelOpen: boolean) {
  const apiClient = useApiClient();

  return useAuthedQuery({
    queryKey: queryKeys.supportSummary,
    queryFn: () => apiClient.api.supportControllerGetSummary(),
    refetchInterval: isPanelOpen ? OPEN_POLL_MS : CLOSED_POLL_MS,
    staleTime: 0,
  });
}

function mergeIntoTail(
  queryClient: QueryClient,
  message: SupportUserMessage,
): void {
  queryClient.setQueryData<SupportUserMessageList>(
    queryKeys.supportMessagesTail,
    (previous) => {
      if (!previous) return { items: [message], nextCursor: null };
      if (previous.items.some((item) => item.id === message.id)) return previous;
      return { ...previous, items: [...previous.items, message] };
    },
  );
}

export interface SupportConversation {
  messages: SupportUserMessage[];
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
  canLoadOlder: boolean;
  isLoadingOlder: boolean;
  loadOlder: () => void;
}

/**
 * The conversation as the panel shows it: the newest page is a polled
 * query (so a reopened panel paints from cache instantly), older pages
 * are walked backwards on demand and kept beside it. Paging the tail
 * query itself would re-fetch every loaded page on every poll.
 */
export function useSupportConversation(isPanelOpen: boolean): SupportConversation {
  const apiClient = useApiClient();
  const [history, setHistory] = useState<{
    items: SupportUserMessage[];
    cursor: string | null;
  } | null>(null);
  const [isLoadingOlder, setIsLoadingOlder] = useState(false);

  const tail = useAuthedQuery({
    queryKey: queryKeys.supportMessagesTail,
    queryFn: () => apiClient.api.supportControllerGetMessages({ limit: PAGE_SIZE }),
    enabled: isPanelOpen,
    refetchInterval: isPanelOpen ? OPEN_POLL_MS : false,
    staleTime: 0,
  });

  const olderCursor = history ? history.cursor : (tail.data?.nextCursor ?? null);

  const messages = useMemo(() => {
    const tailItems = tail.data?.items ?? [];
    if (!history) return tailItems;
    const inTail = new Set(tailItems.map((item) => item.id));
    return [...history.items.filter((item) => !inTail.has(item.id)), ...tailItems];
  }, [tail.data, history]);

  const loadOlder = useCallback(() => {
    if (!olderCursor || isLoadingOlder) return;
    setIsLoadingOlder(true);
    apiClient.api
      .supportControllerGetMessages({ before: olderCursor, limit: PAGE_SIZE })
      .then((page) => {
        setHistory((previous) => ({
          items: [...page.items, ...(previous?.items ?? [])],
          cursor: page.nextCursor ?? null,
        }));
      })
      .finally(() => setIsLoadingOlder(false));
  }, [apiClient, olderCursor, isLoadingOlder]);

  return {
    messages,
    isLoading: tail.isPending,
    isError: tail.isError,
    refetch: tail.refetch,
    canLoadOlder: olderCursor !== null,
    isLoadingOlder,
    loadOlder,
  };
}

export function useSendSupportMessage() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (text: string) => apiClient.api.supportControllerSendMessage({ text }),
    onSuccess: (message) => {
      mergeIntoTail(queryClient, message);
      queryClient.invalidateQueries({ queryKey: queryKeys.supportSummary });
    },
    // A refused send means the summary the composer trusts is out of date:
    // re-ask, so the panel says why instead of offering another attempt.
    onError: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.supportSummary });
    },
  });
}

/**
 * The generated client declares the multipart endpoint without a body —
 * the backend takes the first file part whatever it is named — so the
 * upload is posted by hand against the same origin and CSRF header the
 * generated client uses.
 */
export function useSendSupportFile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ file, caption }: { file: File; caption?: string }) => {
      const body = new FormData();
      body.append('file', file);
      if (caption) body.append('caption', caption);

      const response = await fetch(`${getApiBaseUrl()}/api/users/support/attachments`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'X-Requested-With': 'shot' },
        body,
      });

      if (!response.ok) {
        throw new Error(`Support upload failed with ${response.status}`);
      }

      return (await response.json()) as SupportUserMessage;
    },
    onSuccess: (message) => {
      mergeIntoTail(queryClient, message);
      queryClient.invalidateQueries({ queryKey: queryKeys.supportSummary });
    },
    onError: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.supportSummary });
    },
  });
}

export function useMarkSupportRead() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => apiClient.api.supportControllerMarkRead(),
    onSuccess: (summary) => {
      queryClient.setQueryData(queryKeys.supportSummary, summary);
    },
  });
}
